import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, TrendingUp, DollarSign, FileText } from 'lucide-react';
import { loanService } from '@/services/loan.service';
import { valuationService } from '@/services/valuation.service';
import { CashflowTab } from '@/pages/loanDetails/ui/CashflowTab';
import { PricingTab } from '@/pages/loanDetails/ui/PricingTab';
import { OverviewTab } from '@/pages/loanDetails/ui/OverviewTab';
import { DocumentsTab } from '@/pages/loanDetails/ui/DocumentsTab';
import { LoanHeader } from '@/pages/loanDetails/ui/LoanHeader';
import { TabsNav, type TabItem } from '@/pages/loanDetails/ui/TabsNav';
import type { LoanDetailResponse, LoanResponse } from '@/types/api.types';
import type {
  PricingComponents,
  PricingSummary,
} from '@/services/valuation.service';
const tabs: TabItem[] = [
  { label: 'Overview', icon: <Building2 className="w-4 h-4" /> },
  { label: 'Pricing & Valuation', icon: <TrendingUp className="w-4 h-4" /> },
  { label: 'Cashflow Schedule', icon: <DollarSign className="w-4 h-4" /> },
  { label: 'Documents', icon: <FileText className="w-4 h-4" /> },
];

const LoanDetailPage: React.FC = () => {
  const { loanId } = useParams<{ loanId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loan, setLoan] = useState<LoanResponse | null>(null);
  const [loanExtraData, setLoanExtraData] = useState<LoanDetailResponse | null>(
    null
  );
  const [pricingComponents, setPricingComponents] =
    useState<PricingComponents | null>(null);
  const [pricingSummary, setPricingSummary] = useState<PricingSummary | null>(
    null
  );
  const [loading, setLoading] = useState(true);

  const numericLoanId = loanId ? parseInt(loanId, 10) : null;

  const fetchLoanDetails = async () => {
    try {
      setLoading(true);
      if (!numericLoanId) return;

      // Fetch loan basic data
      const loanData = await loanService.getLoanById(numericLoanId);
      setLoan(loanData);
      const loanExtraData = await loanService.getLoanBySystemId(numericLoanId);

      setLoanExtraData(loanExtraData);

      // Fetch valuation data in parallel
      const [pricingData, pricingSummary] = await Promise.all([
        valuationService
          .getPricingComponents(numericLoanId)
          .then(
            (data) => data.find((p) => p.loan_id === numericLoanId) || null
          ),
        valuationService.getPricingSummary(),
      ]);

      setPricingComponents(pricingData);
      setPricingSummary(pricingSummary[0]);
    } catch (error) {
      console.error('Error fetching loan details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loanId) {
      fetchLoanDetails();
    }
  }, [loanId]);

  if (loading) {
    return (
      <div
        className="flex justify-center items-center h-screen"
        data-test-id="loading-spinner"
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!loan) {
    return (
      <div
        className="container mx-auto px-4 py-8"
        data-test-id="loan-not-found"
      >
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900">
            Loan not found
          </h2>
          <button
            onClick={() => navigate('/loans')}
            className="mt-4 text-blue-600 hover:text-blue-800"
            data-test-id="back-to-results"
          >
            Back to results
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="container mx-auto px-4 py-8"
      data-test-id="loan-detail-page"
    >
      {/* Header */}
      <LoanHeader
        onBack={() => navigate(-1)}
        loanName={loan.loan_name || `Loan ${loan.rp_system_id}`}
        loanIdLabel={loan.client_loan_number || loan.rp_system_id}
        currentBalance={loan.current_balance}
        status={loan.loan_status}
        currency={loan.currency || 'USD'}
      />

      {/* Tabs */}
      <TabsNav
        loanId={numericLoanId ?? 0}
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {/* Tab Content */}
      <div className="mt-6" data-test-id="tab-content">
        {activeTab === 0 && (
          <OverviewTab loan={loan} loanExtraData={loanExtraData} />
        )}

        {activeTab === 1 && (
          <PricingTab 
            summary={pricingSummary} 
            pricing={pricingComponents} 
            currency={loan.currency || 'USD'}
          />
        )}

        {activeTab === 2 && numericLoanId && (
          <CashflowTab loanId={numericLoanId} currency={loan.currency || 'USD'} />
        )}

        {activeTab === 3 && <DocumentsTab />}
      </div>
    </div>
  );
};

export default LoanDetailPage;
